import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_my_reviews,
  create_review,
  update_review,
  delete_review,
  report_review,
  fetch_public_reviews,
  fetch_vendor_reviews,
  reply_to_review,
} from './api';
import type { ReviewPage, UpsertReviewInput } from './types';

const MY_REVIEWS_KEY = ['my-reviews'] as const;

/** All of the owner's reviews, keyed by booking_id for inline My-Bookings state. */
export function use_my_reviews() {
  return useQuery({
    queryKey: MY_REVIEWS_KEY,
    queryFn: fetch_my_reviews,
    select: (rows) => new Map(rows.map((r) => [r.booking_id, r])),
  });
}

function invalidate_owner(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: MY_REVIEWS_KEY });
  qc.invalidateQueries({ queryKey: ['my-bookings'] });
}

export function use_create_review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ booking_id, input }: { booking_id: string; input: UpsertReviewInput }) => create_review(booking_id, input),
    onSuccess: () => invalidate_owner(qc),
  });
}

export function use_update_review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ booking_id, input }: { booking_id: string; input: UpsertReviewInput }) => update_review(booking_id, input),
    onSuccess: () => invalidate_owner(qc),
  });
}

export function use_delete_review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (booking_id: string) => delete_review(booking_id),
    onSuccess: () => invalidate_owner(qc),
  });
}

export function use_report_review() {
  return useMutation({
    mutationFn: ({ review_id, reason }: { review_id: string; reason?: string }) => report_review(review_id, reason),
  });
}

function next_page(last: ReviewPage<unknown>, pages: ReviewPage<unknown>[]): number | undefined {
  const loaded = pages.reduce((n, p) => n + p.items.length, 0);
  return loaded < last.total ? last.page + 1 : undefined;
}

/** Public, paginated reviews on a vendor detail page. */
export function use_public_reviews(vendor_id?: string) {
  return useInfiniteQuery({
    queryKey: ['public-reviews', vendor_id],
    queryFn: ({ pageParam }) => fetch_public_reviews(vendor_id as string, pageParam),
    initialPageParam: 1,
    getNextPageParam: next_page,
    enabled: !!vendor_id,
  });
}

/** Vendor's own reviews, paginated. */
export function use_vendor_reviews() {
  return useInfiniteQuery({
    queryKey: ['vendor-reviews'],
    queryFn: ({ pageParam }) => fetch_vendor_reviews(pageParam),
    initialPageParam: 1,
    getNextPageParam: next_page,
  });
}

export function use_reply_to_review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ review_id, reply }: { review_id: string; reply: string }) => reply_to_review(review_id, reply),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-reviews'] }),
  });
}
